<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  // Opt-in (off by default so existing callers, e.g. the one-off campaign
  // dialog, keep today's plain-text-only behaviour). When on, each body
  // variable also gets a picker that inserts a Liquid tag the backend
  // (Whatsapp::LiquidTemplateProcessorService) already renders per
  // recipient at dispatch time - no backend change needed for this.
  allowDynamicFields: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'sendMessage',
  'scheduleMessage',
  'resetTemplate',
  'back',
]);

const { t, te } = useI18n();
// Categorias vêm da Meta em inglês (UTILITY, AUTHENTICATION...); categoria desconhecida
// é exibida como veio.
const translateCategory = category => {
  const key = `INBOX_MGMT.MESSAGE_TEMPLATES.CATEGORY_OPTIONS.${(category || '').toUpperCase()}`;
  return te(key) ? t(key) : category;
};

const processedParams = ref({});

// Mirrors ContactDrop/CompanyDrop/DealDrop (app/drops/*.rb) - keep in sync if
// those gain/lose fields. `custom_attribute` is left out: it needs a per-key
// picker this MVP doesn't build yet, typing it by hand still works.
const DYNAMIC_FIELD_OPTIONS = [
  {
    value: '{{ contact.name }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.CONTACT_NAME'),
  },
  {
    value: '{{ contact.first_name }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.CONTACT_FIRST_NAME'),
  },
  {
    value: '{{ contact.last_name }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.CONTACT_LAST_NAME'),
  },
  {
    value: '{{ contact.email }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.CONTACT_EMAIL'),
  },
  {
    value: '{{ contact.phone_number }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.CONTACT_PHONE'),
  },
  {
    value: '{{ company.name }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.COMPANY_NAME'),
  },
  {
    value: '{{ company.domain }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.COMPANY_DOMAIN'),
  },
  {
    value: '{{ company.cnpj }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.COMPANY_CNPJ'),
  },
  {
    value: '{{ company.city }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.COMPANY_CITY'),
  },
  {
    value: '{{ deal.name }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.DEAL_NAME'),
  },
  {
    value: '{{ deal.value }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.DEAL_VALUE'),
  },
  {
    value: '{{ deal.pipeline }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.DEAL_PIPELINE'),
  },
  {
    value: '{{ deal.pipeline_stage }}',
    label: t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELDS.DEAL_STAGE'),
  },
];

const insertDynamicField = (key, value) => {
  if (!value) return;
  processedParams.value.body[key] = value;
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${translateCategory(props.template.category || DEFAULT_CATEGORY)}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

// When this template was created in Corvus with named variables (spec 133 v4), the
// approved/synced copy only has positional placeholders ({{1}}, {{2}}) — this map lets
// the fill-in fields show the friendly name instead of the raw position.
const variableLabelByPosition = computed(() => {
  const labels = props.template.variable_labels || [];
  return labels.reduce((map, label) => {
    map[String(label.position)] = label.name;
    return map;
  }, {});
});

const variableLabel = key => variableLabelByPosition.value[key] || key;

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const buildPayload = () => {
  const { name, category, language, namespace } = props.template;

  return {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('sendMessage', buildPayload());
};

const scheduleMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('scheduleMessage', buildPayload());
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  scheduleMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center gap-2 mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: variableLabel(key),
              })
            "
          />
          <!-- ComboBox's own root always carries `w-full`, which wins the
          Tailwind cascade over a width class passed in from here - sizing it
          via a wrapper is the reliable way to keep it from filling the row. -->
          <div v-if="allowDynamicFields" class="w-56 shrink-0">
            <ComboBox
              model-value=""
              :options="DYNAMIC_FIELD_OPTIONS"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.DYNAMIC_FIELD_PLACEHOLDER')
              "
              @update:model-value="value => insertDynamicField(key, value)"
            />
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :schedule-message="scheduleMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
